<template>
  <Dialog
    class="offer-details-modal"
    v-bind="$attrs"
    hide-title
    :persisted="true"
    :is-fullscreen-mobile="true"
  >
    <template #header>
      <button v-if="displayFaqs" @click="$emit('openFaqs')">
        <BaseIcon name="help-mono" size="sm" />
      </button>
    </template>

    <template #content>
      <div class="partner-details">
        <partner-logo
          v-if="cptdCurrentPartner"
          :partner-id="cptdCurrentPartner"
        />

        <h2 class="partner-name">
          {{ cptdPartnerName }}
        </h2>
        <Badge class="product-type" type="neutral">
          {{ productType }}
        </Badge>
      </div>
      <div class="conditions">
        <slot name="conditions">
          <article>
            <p class="condition">Empréstimo</p>
            <p class="value">
              {{ cptdOfferAmountBrl }}
            </p>
          </article>

          <article>
            <p class="condition">Parcelas</p>
            <p class="value">
              {{ cptdOfferDuration }}x de
              {{ cptdInstallmentAmountBrl }}
            </p>
          </article>

          <!-- TODO: we need to rise up the types of payments before to implement this item -->
          <!-- <article>
            <p class="condition">Forma de pagamento</p>
            <p class="value">
              PIX, boleto e débito em conta
            </p>
          </article> -->

          <article>
            <p class="condition">Juros ao mês</p>
            <p class="value">{{ cptdMonthlyInterestRate }}%</p>
          </article>

          <article v-if="cptdAnnualInterestRate">
            <p class="condition">Juros ao ano</p>
            <p class="value">{{ cptdAnnualInterestRate }}%</p>
          </article>

          <article
            v-for="(feeDetail, index) in cptdOfferFeeDetails"
            :key="index"
          >
            <p class="condition">{{ feeDetail.name }}</p>
            <p class="value">
              {{ feeDetail.value }}
            </p>
          </article>

          <article v-if="cptdCetRateMonthly">
            <p class="condition">CET ao mês</p>
            <p class="value">{{ cptdCetRateMonthly }}%</p>
          </article>

          <article v-if="cptdCetRateAnnually">
            <p class="condition">CET ao ano</p>
            <p class="value">{{ cptdCetRateAnnually }}%</p>
          </article>

          <article>
            <p class="condition">Total a pagar</p>
            <p class="value">
              {{ cptdTotalAmountBrl }}
            </p>
          </article>

          <article>
            <p class="condition">Taxas + Juros</p>
            <p class="value">
              {{ cptdTotalInterestAmountBrl }}
            </p>
          </article>
        </slot>
      </div>

      <div class="benefits">
        <div
          v-for="(item, index) in cptdBenefits"
          :key="index"
          class="benefit"
          v-html="sanitizeHtml(item.description)"
        />
      </div>
    </template>

    <template #footer>
      <slot name="actions">
        <Button :busy="busy" @click="$emit('submit')"> Continuar </Button>
      </slot>
      <slot name="other-actions" />
    </template>
  </Dialog>
</template>
<script>
import allBenefits from '@/helpers/benefits'
import { parseToBRL } from '@/helpers/currency'
import Dialog from '@/aasgard/components/modals/Dialog.vue'
import BaseIcon from '@/aasgard/components/base/BaseIcon.vue'
import PartnerLogo from '@/components/appForm/loanResults/PartnerLogo.vue'
import Button from '@/aasgard/components/buttons/Button.vue'
import Badge from '@/aasgard/components/feedback/Badge.vue'
import { sanitizeHtmlContent } from '~/helpers/html-sanitize'
import { Partner } from '~/data/common'
import { getTranslatedOfferFeeDetails } from '~/helpers/offers'
import { PartnersName } from '~/helpers/partners/common'
export default {
  name: 'OfferDetailsModal',
  components: {
    Dialog,
    BaseIcon,
    PartnerLogo,
    Button,
    Badge,
  },
  props: {
    offer: { type: [Array, Object], default: () => ({}) },
    busy: { type: Boolean, default: false },
    displayFaqs: { type: Boolean, default: false },
    productType: { type: String, default: 'Empréstimo sem garantia' },
  },
  emits: ['openFaqs', 'submit'],
  data() {
    return {
      partnerInfo: {},
      benefits: {},
    }
  },
  computed: {
    cptdCurrentPartner() {
      return this.offer?.partner
    },
    cptdOfferAmountBrl() {
      return this.mxParseToBRL(this?.offer?.amount)
    },
    cptdInstallmentAmountBrl() {
      return this.mxParseToBRL(
        this.offer?.installment_amount ?? this.offer?.installment_value,
      )
    },
    cptdMonthlyInterestRate() {
      return (
        this.offer?.display_interest_rate_monthly ??
        this.offer?.interest_rate_monthly ??
        0
      )
        ?.toFixed(2)
        ?.replace('.', ',')
    },
    cptdAnnualInterestRate() {
      const { product_type, interest_rate_annually } = this.offer || {}
      if (product_type === 'auto' && interest_rate_annually) {
        return (interest_rate_annually ?? 0)?.toFixed(2)?.replace('.', ',')
      }

      return 0
    },
    cptdCetRateMonthly() {
      const { product_type, cet_rate } = this.offer || {}
      if (product_type === 'auto' && cet_rate) {
        return (cet_rate ?? 0)?.toFixed(2)?.replace('.', ',')
      }
      return 0
    },
    cptdCetRateAnnually() {
      const { product_type, cet_rate } = this.offer || {}
      if (product_type === 'auto' && cet_rate) {
        return (((cet_rate / 100 + 1) ** 12 - 1) * 100)
          ?.toFixed(2)
          ?.replace('.', ',')
      }

      return 0
    },
    cptdTotalAmountBrl() {
      const value = this.offer?.duration * this.offer?.installment_amount || 0
      return this.mxParseToBRL(value)
    },
    cptdTotalInterestAmountBrl() {
      const paidValue =
        this.offer?.duration * this.offer?.installment_amount || 0
      const offerAmount = this?.offer?.amount

      return this.mxParseToBRL(paidValue - offerAmount)
    },
    cptdOfferDuration() {
      return this.offer?.duration ?? ''
    },
    cptdPartnerName() {
      return PartnersName[this.cptdCurrentPartner ?? ''] ?? ''
    },
    cptdBenefits() {
      const benefits =
        allBenefits.find((v) => {
          return (
            v?.label?.toLowerCase() === this.cptdCurrentPartner?.toLowerCase()
          )
        }) || {}
      return benefits.items || []
    },
    cptdOfferFeeDetails() {
      if (this.cptdCurrentPartner === Partner.BV) {
        return getTranslatedOfferFeeDetails(this.offer?.fees_details)
      }

      return []
    },
  },
  methods: {
    sanitizeHtmlContent,
    mxParseToBRL(value) {
      return parseToBRL(value)
    },
    sanitizeHtml(value) {
      return this.sanitizeHtmlContent(value)
    },
  },
}
</script>
<style lang="scss">
.offer-details-modal {
  &.Dialog {
    & > .content-wrapper {
      & > .content {
        & > .partner-details {
          background-color: $color-neutral-light;
          padding: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          border-radius: 12px;

          & > svg {
            margin-bottom: 16px;
            border-radius: 50%;
            width: 56px;
            height: 56px;
          }

          & > .partner-name {
            @include headline-5;
            color: $color-black-high;
            text-align: center;
            margin: 0 0 8px 0;
          }
        }

        & > .conditions {
          padding: 16px 0;
          & > article {
            margin-bottom: 20px;

            &:last-child {
              margin: 0;
            }

            & > .condition {
              @include body-2;
              color: $color-black-medium;
              margin: 0 0 4px;
            }

            & > .value {
              @include subtitle-1;
              color: $color-black-high;
              margin: 0;
            }
          }
        }

        & > .benefits {
          width: 100%;
          background-color: $color-neutral-light;
          padding: 16px;
          border-radius: 8px;

          & > .benefit {
            margin: 0;
            @include body-2;
            color: $color-black-medium;

            &:not(:last-child) {
              padding-bottom: 16px;
              margin-bottom: 16px;
              border-bottom: 1px solid $color-neutral-dark;
            }

            & > a {
              color: $color-brand-primary;
              @include body-2;
            }
          }
        }
      }
    }
  }
}
</style>
